// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import {
  ThemeProvider,
  createTheme,
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel
} from '@mui/material';

/**
 * 難易度に応じた計算式を生成する関数
 * 
 * - easy   : 項数固定2、使用演算子は「+」「-」、数値は 2～10 の範囲
 * - medium : 項数は2～3、使用演算子は「+」「-」「*」、数値は 2～20 の範囲
 * - hard   : 項数は3～4、使用演算子は「+」「-」「*」「/」、数値は 2～50 の範囲
 *
 * 式は左から順に計算させるため、各ステップを括弧で囲んでいます。
 */
function generateExpression(difficulty) {
  let numOperands, minVal, maxVal, allowedOps;
  if (difficulty === 'easy') {
    numOperands = 2;
    minVal = 2;
    maxVal = 10;
    allowedOps = ['+', '-'];
  } else if (difficulty === 'medium') {
    numOperands = Math.floor(Math.random() * 2) + 2; // 2～3項
    minVal = 2;
    maxVal = 20;
    allowedOps = ['+', '-', '*'];
  } else if (difficulty === 'hard') {
    numOperands = Math.floor(Math.random() * 2) + 3; // 3～4項
    minVal = 2;
    maxVal = 50;
    allowedOps = ['+', '-', '*', '/'];
  } else {
    // 万が一のためのデフォルト設定
    numOperands = 2;
    minVal = 2;
    maxVal = 10;
    allowedOps = ['+', '-'];
  }

  // 初項
  let value = Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal;
  let expr = value.toString();

  for (let i = 1; i < numOperands; i++) {
    let op = allowedOps[Math.floor(Math.random() * allowedOps.length)];
    let nextNum;
    if (op === '/') {
      // 現在の value を割り切れる数（minVal～maxVal内）を候補にする
      let divisors = [];
      for (let n = minVal; n <= Math.min(maxVal, value); n++) {
        if (value % n === 0) {
          divisors.push(n);
        }
      }
      if (divisors.length === 0) {
        // 割り切れる数が見つからなければ、割り算以外の演算子にフォールバック
        const fallbackOps = allowedOps.filter(o => o !== '/');
        op = fallbackOps[Math.floor(Math.random() * fallbackOps.length)];
        nextNum = Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal;
      } else {
        nextNum = divisors[Math.floor(Math.random() * divisors.length)];
      }
    } else {
      nextNum = Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal;
    }
    // 左から順に計算させるため、括弧で囲む
    expr = `(${expr} ${op} ${nextNum})`;
    try {
      value = eval(expr);
    } catch (e) {
      value = 0;
    }
  }
  return { expression: expr, answer: value };
}

function MathQuizView() {
  // 画面管理：'start'（スタート画面）／'quiz'（クイズ中）／'result'（結果発表）
  const [view, setView] = useState('start');
  // 内部で決めるパラメータ（難易度により変更）
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [timeLimit, setTimeLimit] = useState(15);

  // ユーザー選択の難易度（初期値：easy）
  const [difficulty, setDifficulty] = useState('easy');

  // クイズ用の状態
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null); // { expression, answer }
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [resultMessage, setResultMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(timeLimit);

  // canvas 用の ref（計算式を描画）
  const canvasRef = useRef(null);

  // 問題生成時に、canvas に計算式（隠蔽された文字列）を描画（SFテイスト：黒背景＋ネオン文字）
  useEffect(() => {
    if (view === 'quiz' && currentQuestion) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        // キャンバスをクリアして黒で塗りつぶす
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // 計算式を描画（フォントはモノスペースでデジタル感を演出）
        ctx.font = '24px monospace';
        ctx.fillStyle = '#00e676'; // ネオングリーン
        const text = currentQuestion.expression;
        const textWidth = ctx.measureText(text).width;
        const x = (canvas.width - textWidth) / 2;
        const y = canvas.height / 2 + 8; // 文字のベースライン調整
        ctx.fillText(text, x, y);
      }
    }
  }, [currentQuestion, view]);

  // クイズ中は、1秒ごとにカウントダウンするタイマーを動作させる
  useEffect(() => {
    if (view === 'quiz') {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev > 1) return prev - 1;
          else {
            clearInterval(timer);
            handleTimeOut();
            return 0;
          }
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [view, currentQuestion]);

  // 時間切れ時の処理
  const handleTimeOut = () => {
    if (resultMessage === '') {
      setResultMessage('時間切れです！');
    }
  };

  // 新しい問題を生成／読み込み
  const loadNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions) {
      const newQuestion = generateExpression(difficulty);
      setCurrentQuestion(newQuestion);
      setUserAnswer('');
      setResultMessage('');
      setTimeLeft(timeLimit);
      // 問題番号は 1 からカウント
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // 全問題終了時は結果画面へ
      setView('result');
    }
  };

  // 回答送信時の処理
  const handleSubmit = (e) => {
    e.preventDefault();
    if (timeLeft <= 0 || resultMessage !== '') return;
    const numericUserAnswer = parseFloat(userAnswer);
    if (numericUserAnswer === currentQuestion.answer) {
      setResultMessage('正解です！');
      setScore(prev => prev + 1);
    } else {
      setResultMessage('不正解です。');
    }
  };

  // スタート画面で「開始」ボタン押下時の処理
  const startQuiz = (e) => {
    e.preventDefault();
    // 内部で難易度に応じた問題数・制限時間を決定
    let settings;
    if (difficulty === 'easy') {
      settings = { totalQuestions: 5, timeLimit: 15 };
    } else if (difficulty === 'medium') {
      settings = { totalQuestions: 7, timeLimit: 10 };
    } else if (difficulty === 'hard') {
      settings = { totalQuestions: 10, timeLimit: 7 };
    } else {
      settings = { totalQuestions: 5, timeLimit: 15 };
    }
    setTotalQuestions(settings.totalQuestions);
    setTimeLimit(settings.timeLimit);
    setScore(0);
    setCurrentQuestionIndex(0);
    setView('quiz');
  };

  // クイズ開始直後に最初の問題をロード
  useEffect(() => {
    if (view === 'quiz' && currentQuestionIndex === 0) {
      loadNextQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // 各画面のレンダリング
  if (view === 'start') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            padding: 4,
            textAlign: 'center',
            backgroundColor: '#1e1e1e',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          <Typography variant="h3" sx={{ color: '#00e676' }}>
            数学クイズゲーム
          </Typography>
          <Box component="form" onSubmit={startQuiz} sx={{ mt: 4 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ color: '#00e676', mb: 1 }}>
                難易度を選択
              </FormLabel>
              <RadioGroup
                row
                name="difficulty"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <FormControlLabel value="easy" control={<Radio />} label="イージー" />
                <FormControlLabel value="medium" control={<Radio />} label="ミディアム" />
                <FormControlLabel value="hard" control={<Radio />} label="ハード" />
              </RadioGroup>
            </FormControl>
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
              開始
            </Button>
          </Box>
        </Box>
      </Container>
    );
  } else if (view === 'quiz') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            padding: 4,
            textAlign: 'center',
            backgroundColor: '#1e1e1e',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          <Typography variant="h4" sx={{ color: '#00e676' }}>
            問題 {currentQuestionIndex} / {totalQuestions}
          </Typography>
          {/* 計算式は canvas にレンダリング */}
          <canvas
            ref={canvasRef}
            width={300}
            height={100}
            style={{ border: '1px solid #00e676', margin: '20px 0' }}
          />
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            残り時間: {timeLeft}秒
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              label="答えを入力"
              variant="outlined"
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
              disabled={timeLeft <= 0 || resultMessage !== ''}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={timeLeft <= 0 || resultMessage !== ''}
            >
              回答する
            </Button>
          </Box>
          {resultMessage && (
            <Typography variant="h6" sx={{ mt: 2, color: '#00e676' }}>
              {resultMessage}
            </Typography>
          )}
          {resultMessage !== '' && (
            <Button onClick={loadNextQuestion} variant="outlined" color="primary" sx={{ mt: 2 }}>
              次の問題へ
            </Button>
          )}
        </Box>
      </Container>
    );
  } else if (view === 'result') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            padding: 4,
            textAlign: 'center',
            backgroundColor: '#1e1e1e',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          <Typography variant="h3" sx={{ color: '#00e676' }}>
            結果発表
          </Typography>
          <Typography variant="h5" sx={{ mt: 2, color: '#ffffff' }}>
            あなたのスコア: {score} / {totalQuestions}
          </Typography>
          <Button
            onClick={() => window.location.reload()}
            variant="contained"
            color="primary"
            sx={{ mt: 4 }}
          >
            もう一度
          </Button>
        </Box>
      </Container>
    );
  }
}

// ダークモード・SFテイストのテーマ設定（背景は黒／暗いグレー、アクセントはネオンカラー）
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00e676' // ネオングリーン
    },
    secondary: {
      main: '#00b0ff' // ネオンブルー
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e'
    }
  },
  typography: {
    fontFamily: 'Roboto, sans-serif'
  }
});

// テーマプロバイダーでラップしてエクスポート
export default function MathQuiz() {
  return (
    <ThemeProvider theme={theme}>
      <MathQuizView />
    </ThemeProvider>
  );
}
